// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetSurveyResponse } from '../../../../../../shareables/types/api/main/website/page/type/220/survey/get';
import Table from '../shared-components/table';
import EmptyStateRow from '../shared-components/table/empty-state-row';


// Styled components
const Container = styled.div`
	h3:not(:first-child) {
		margin-top: 1.875rem;
	}
`;

const NoSurveyChosen = styled.div`
	margin-top: -0.0938rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookQuestions?: GetSurveyResponse['questions'];
}


// Function component
const SurveyResults: React.FC<Props> = ({ pageID, storybookQuestions }) => {
	// Use state
	const [questions, setQuestions] = useState<GetSurveyResponse['questions'] | null>(storybookQuestions ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load questions on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetSurveyResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/220/survey`,
				completion: (json) => {
					setQuestions(json.response.questions);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// Return JSX
	return (
		<Container>
			{questions?.length === 0 && (
				<NoSurveyChosen>
					<em>No survey has been chosen by an admin yet…</em>
				</NoSurveyChosen>
			)}
			{questions?.map((question, questionIndex) => (
				<React.Fragment key={questionIndex}>
					<h3>{question.prompt}</h3>
					
					<Table>
						<thead>
							<tr>
								<th>Answer</th>
								{question.type === 'PRESET' && (
									<React.Fragment>
										<th>Count</th>
										<th>Percentage</th>
									</React.Fragment>
								)}
							</tr>
						</thead>
						<tbody>
							{question.type === 'PRESET' ? (
								<React.Fragment>
									{Object.entries(question.results).map(([choice, count], resultIndex) => (
										<tr key={resultIndex}>
											<td>{choice}</td>
											<td>{count}</td>
											<td>
												{Object.values(question.results).reduce((acc, result) => acc + result) === 0
													? 0
													: Math.floor(
															(count / Object.values(question.results).reduce((acc, result) => acc + result)) * 1000
														) / 10}
												%
											</td>
										</tr>
									))}
								</React.Fragment>
							) : question.responses.length === 0 ? (
								<EmptyStateRow>No responses yet…</EmptyStateRow>
							) : (
								question.responses.map((response, responseIndex) => (
									<tr key={responseIndex}>
										<td style={{ overflowWrap: 'anywhere' }}>
											<em>{response}</em>
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</React.Fragment>
			))}
		</Container>
	);
};

export default SurveyResults;
